import { useEffect, useRef, useState } from 'react'
import { api } from '../../../lib/api.js'
import { normalizeSchools } from '../../../lib/schools.js'
import { refLabel } from './buildPayload.js'
import { uploadOwnedFile } from '../ownedAsset.js'
import { attachFailure, refLine } from './assetFailureCopy.js'
import styles from './Studio.module.css'

/**
 * STEP 1 — THE BEZEL LOGO (C). Master Spec Section 4, step 1.
 *
 * C is the one component that is NOT sourced from Canva: it is the client's
 * real crest, and it has to be the OWNED asset (HubSpot File Manager) or an
 * asset the operator attaches — never a hot-linked mark from someone else's
 * site, which is a dead link waiting to poison the record.
 *
 * Selecting a school here does two jobs: it sets C, and it hands the school
 * record back up so the studio can SUGGEST (never autofill) team colors and the
 * presentation line from it.
 *
 * The backend refuses a build with no C, so this step is genuinely required —
 * the studio blocks on it rather than posting a request it knows will 400.
 */
export default function LogoCPicker({ value, onChange, onSchool, school }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [busy, setBusy] = useState(false)
  const [findMsg, setFindMsg] = useState('')
  const [manual, setManual] = useState('')
  const [uploading, setUploading] = useState(false)
  const [retrying, setRetrying] = useState('')
  const abortRef = useRef(null)
  const fileRef = useRef(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setRows([]); return undefined }
    const t = setTimeout(async () => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      setBusy(true)
      try {
        const res = await api.schools(term, { signal: ctrl.signal })
        setRows(normalizeSchools(res).slice(0, 8))
      } catch (err) {
        if (err?.name !== 'AbortError') setRows([])
      } finally {
        setBusy(false)
      }
    }, 260)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => () => abortRef.current?.abort(), [])

  function chooseSchool(s) {
    if (!s.logoUrl) {
      setFindMsg(`${s.name} has no owned logo on file. Use “Find the owned mark” or attach the crest below — the engine will not invent one.`)
    } else {
      setFindMsg('')
    }
    onSchool?.(s)
    if (s.logoUrl) {
      onChange({ id: s.id, name: `${s.name} crest`, source: 'HubSpot-owned', url: s.logoUrl })
      setRows([])
      setQ('')
    }
  }

  async function findOwned() {
    const name = (school?.name || q).trim()
    if (!name) return
    setBusy(true)
    setFindMsg('')
    try {
      const res = await api.logoFind({ name })
      if (res?.found && res?.logo) {
        onChange({
          name: `${res.matched || name} crest`,
          source: res.owned ? 'HubSpot-owned' : 'sourced — owned copy staged for approval',
          url: res.logo,
        })
        setFindMsg(res.owned
          ? 'Matched an owned mark.'
          : 'Sourced and staged an owned copy — it goes to the Marks Library queue for approval.')
      } else {
        setFindMsg(`No mark found for “${name}”. Attach the crest below.`)
      }
    } catch (err) {
      setFindMsg(`The mark finder is unavailable (${err?.status || 'network error'}). Attach the crest below.`)
    } finally {
      setBusy(false)
    }
  }

  /* ⚠️ FAILS CLOSED. Same defect as CanvaFolderPicker#upload carried, and this
   * is the crest slot, which every recent build actually uses. It used to
   * attach `{ name, source: 'manual reference — not yet owned' }` on failure,
   * putting a named crest on a build with no file behind it. Nothing is
   * attached now; the rep sees a blank and the button is still there. */
  async function upload(file) {
    if (!file) return
    setUploading(true)
    setRetrying('')
    try {
      const owned = await uploadOwnedFile(file, {
        kind: 'crest',
        onAttempt: ({ attempt, of }) => setRetrying(`Storage did not answer. Retrying (${attempt + 1} of ${of})…`),
      })
      onChange({ id: owned.fileId, name: file.name, source: 'uploaded — owned in HubSpot File Manager', url: owned.url })
      setFindMsg('')
    } catch (err) {
      const f = attachFailure(file?.name, err)
      setFindMsg(`${f.headline} ${f.fix}${f.ref ? ` ${refLine(f.ref)}` : ''}`)
    } finally {
      setUploading(false)
      setRetrying('')
    }
  }

  const chosen = refLabel(value)

  return (
    <div className={styles.logoStep}>
      <div className={styles.logoTop}>
        <div className={styles.logoPreview}>
          {value?.url
            ? <img src={value.url} alt={chosen} />
            : <span className={styles.logoEmpty}>C</span>}
        </div>
        <div className={styles.logoMeta}>
          {chosen ? (
            <>
              <div className={styles.logoName}>{chosen}</div>
              <div className={styles.pickerSrc}>{value.source}</div>
              <button type="button" className={styles.linkBtn} onClick={() => onChange(null)}>Clear</button>
            </>
          ) : (
            <div className={styles.pickerEmpty}>
              No crest selected. The bezel design carries a placeholder logo and the prompt replaces it with C.
            </div>
          )}
        </div>
      </div>

      <label className="field-label" htmlFor="logo-c-search">Search HubSpot for the owned crest</label>
      <input
        id="logo-c-search"
        className="input"
        placeholder="School or organization name"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />
      {busy && <p className={styles.note}>Searching…</p>}

      {!!rows.length && (
        <ul className={styles.schoolList}>
          {rows.map((s) => (
            <li key={s.id}>
              <button type="button" className={styles.schoolRow} onClick={() => chooseSchool(s)}>
                <span className={styles.schoolLogo}>
                  {s.logoUrl ? <img src={s.logoUrl} alt="" /> : <span>{s.name[0]?.toUpperCase()}</span>}
                </span>
                <span className={styles.schoolText}>
                  <span className={styles.schoolName}>{s.name}</span>
                  <span className={styles.schoolLoc}>{s.location || 'Location not on file'}</span>
                </span>
                {!s.logoUrl && <span className={`${styles.badge} ${styles.badgeWarn}`}>no owned logo</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* The crest can ALWAYS be supplied — the owned-mark finder, a typed
          reference, or a file straight off this device into HubSpot File
          Manager. None of it is behind a disclosure, because step 1 is required
          and the engine refuses a build with no C. */}
      <div className={styles.pickerActions}>
        <button type="button" className="btn" onClick={findOwned} disabled={busy || !(school?.name || q).trim()}>
          Find the owned mark
        </button>
        <button type="button" className="btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload the crest from this device'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.svg,.pdf"
          hidden
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = '' }}
        />
      </div>

      <div className={styles.attachRow}>
        <input
          className="input"
          aria-label="Crest reference"
          placeholder="Or describe / reference the crest"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && manual.trim()) {
              e.preventDefault()
              onChange({ name: manual.trim(), source: 'manual reference' })
              setManual('')
            }
          }}
        />
        <button
          type="button"
          className="btn"
          disabled={!manual.trim()}
          onClick={() => { onChange({ name: manual.trim(), source: 'manual reference' }); setManual('') }}
        >
          Attach reference
        </button>
      </div>
      {retrying && <p className={styles.note} role="status">{retrying}</p>}
      {findMsg && <p className={styles.warnNote}>{findMsg}</p>}
    </div>
  )
}
